import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion, type Variants } from 'framer-motion'
import { Lock, ArrowRight, ArrowLeft, Eye, EyeSlash, WarningCircle, Check } from '@phosphor-icons/react'
import { toast } from 'sonner'
import { DotLoader } from '@/components/ui/DotLoader'
import { authApi } from '@/lib/api'
import {
  authInputClass as inputClass,
  authInputStyle as inputStyle,
  errorBorderClass,
} from '@/lib/styleKit'

const resetSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Use upper, lower, and a number'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

type ResetFormData = z.infer<typeof resetSchema>

const easeOutExpo = [0.16, 1, 0.3, 1] as const

const formStageVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
}

const formItemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOutExpo } },
}

const shakeVariants: Variants = {
  idle: { x: 0 },
  error: {
    x: [0, -6, 6, -4, 4, 0],
    transition: { duration: 0.4, ease: 'easeInOut' },
  },
}

/**
 * Terminal state for a link that can never work: no token in the URL, or a token
 * the backend rejected as invalid / expired / already used. Both cases have the
 * same and only useful next action — get a fresh link.
 */
const DeadLinkScreen: React.FC<{ message: string; onRequestNew: () => void }> = ({
  message,
  onRequestNew,
}) => (
  <motion.div className="w-full" variants={formStageVariants} initial="hidden" animate="show">
    <motion.div className="mb-8" variants={formItemVariants}>
      <h1
        className="text-[#0f1011] dark:text-[#f7f8f8]"
        style={{
          fontFamily: "'Instrument Serif', 'Iowan Old Style', 'Apple Garamond', Georgia, serif",
          fontSize: '36px',
          lineHeight: 1.05,
          letterSpacing: '-0.8px',
          fontWeight: 400,
        }}
      >
        Invalid <span className="italic text-[#5e6ad2] dark:text-[#7170ff]">link</span>
      </h1>
    </motion.div>
    <motion.div variants={formItemVariants} className="space-y-6">
      <div
        role="alert"
        className="flex items-start gap-3 rounded-md border border-amber-500/20 bg-amber-500/[0.06] p-4"
      >
        <WarningCircle
          className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5"
          weight="fill"
        />
        <p
          className="text-[#7a5d2a] dark:text-[#e0c089]"
          style={{ fontSize: '13.5px', lineHeight: 1.5, letterSpacing: '-0.13px' }}
        >
          {message}
        </p>
      </div>
      <button
        type="button"
        onClick={onRequestNew}
        className="w-full h-10 rounded-md bg-[#5e6ad2] hover:bg-[#7170ff] active:bg-[#5058c0] text-white inline-flex items-center justify-center gap-2 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#5e6ad2]/40 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#0f1011]"
        style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
      >
        Request a new link
      </button>
    </motion.div>
  </motion.div>
)

/** The zod rule above, restated as a live checklist. */
const PASSWORD_RULES: { label: string; test: (v: string) => boolean }[] = [
  { label: '8+ characters', test: (v) => v.length >= 8 },
  { label: 'Lowercase', test: (v) => /[a-z]/.test(v) },
  { label: 'Uppercase', test: (v) => /[A-Z]/.test(v) },
  { label: 'Number', test: (v) => /\d/.test(v) },
]

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const [showPassword, setShowPassword] = React.useState(false)
  const [showConfirm, setShowConfirm] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  // Set when the backend rejects the token itself. Retrying the form with the
  // same dead token fails identically forever, so we leave the form entirely.
  const [deadLinkMessage, setDeadLinkMessage] = React.useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
  })

  const passwordValue = watch('password') || ''

  const onSubmit = async (data: ResetFormData) => {
    setIsLoading(true)
    try {
      await authApi.resetPassword({ token, new_password: data.password })
      toast.success('Password reset. Please sign in with your new password.')
      navigate('/login', { replace: true })
    } catch (error: any) {
      const detail = error?.response?.data?.detail
      // The backend answers a spent/invalid/expired token with 400 + that copy.
      // Anything else (network drop, 5xx, rate limit) is transient — keep the
      // user on the form and let them retry, which is what a toast is for.
      const tokenIsDead =
        error?.response?.status === 400 ||
        /invalid|expired|already been used/i.test(String(detail ?? ''))
      if (tokenIsDead) {
        setDeadLinkMessage(
          typeof detail === 'string' && detail
            ? detail
            : 'This reset link is invalid or has expired. Please request a new one.',
        )
        return
      }
      toast.error(detail || 'Something went wrong. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // No token in the URL → the link is malformed/incomplete. Same terminal screen
  // as a token the backend rejected: submitting is a guaranteed failure either way.
  if (!token || deadLinkMessage) {
    return (
      <DeadLinkScreen
        message={
          deadLinkMessage ??
          'This password reset link is missing or incomplete. Please request a new one.'
        }
        onRequestNew={() => navigate('/forgot-password')}
      />
    )
  }

  return (
    <motion.div
      className="w-full"
      variants={formStageVariants}
      initial="hidden"
      animate="show"
    >
      {/* Header */}
      <motion.div className="mb-8" variants={formItemVariants}>
        <h1
          className="text-[#0f1011] dark:text-[#f7f8f8]"
          style={{
            fontFamily: "'Instrument Serif', 'Iowan Old Style', 'Apple Garamond', Georgia, serif",
            fontSize: '36px',
            lineHeight: 1.05,
            letterSpacing: '-0.8px',
            fontWeight: 400,
          }}
        >
          Choose a new <span className="italic text-[#5e6ad2] dark:text-[#7170ff]">password</span>
        </h1>
        <p
          className="text-[#5f6166] dark:text-[#8a8f98] mt-2"
          style={{
            fontSize: '15px',
            lineHeight: 1.5,
            letterSpacing: '-0.165px',
            fontWeight: 400,
          }}
        >
          Pick a strong password you don't use anywhere else
        </p>
      </motion.div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* New password */}
        <motion.div variants={formItemVariants}>
          <motion.div
            className="relative group"
            variants={shakeVariants}
            animate={errors.password ? 'error' : 'idle'}
          >
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a7d83] dark:text-[#62666d] group-focus-within:text-[#5e6ad2] dark:group-focus-within:text-[#7170ff] transition-colors pointer-events-none z-10"
              weight="regular"
            />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="New password"
              aria-label="New password"
              aria-invalid={errors.password ? true : undefined}
              aria-describedby={
                errors.password ? 'password-requirements password-error' : 'password-requirements'
              }
              autoFocus
              style={inputStyle}
              className={`${inputClass} pr-10 ${errors.password ? errorBorderClass : ''}`}
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7a7d83] hover:text-[#0f1011] dark:text-[#62666d] dark:hover:text-[#d0d6e0] transition-colors focus:outline-none z-10"
            >
              {showPassword ? <EyeSlash className="h-4 w-4" weight="regular" /> : <Eye className="h-4 w-4" weight="regular" />}
            </button>
          </motion.div>

          {/* The constraint, stated once for assistive tech. The chip row below
              is the sighted-user rendering of the same thing (§56.3). */}
          <p id="password-requirements" className="sr-only">
            Password must be at least 8 characters and include a lowercase letter, an
            uppercase letter, and a number.
          </p>
          <div aria-hidden="true" className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1.5">
            {PASSWORD_RULES.map((rule) => {
              const met = rule.test(passwordValue)
              return (
                <span
                  key={rule.label}
                  className={`inline-flex items-center gap-1 transition-colors duration-150 ${
                    met
                      ? 'text-emerald-700 dark:text-emerald-400'
                      : 'text-[#5f6166] dark:text-[#8a8f98]'
                  }`}
                  style={{ fontSize: '12px', letterSpacing: '-0.1px', fontWeight: 400 }}
                >
                  <Check
                    className={`h-3.5 w-3.5 transition-opacity duration-150 ${met ? 'opacity-100' : 'opacity-40'}`}
                    weight="bold"
                  />
                  {rule.label}
                </span>
              )
            })}
          </div>

          {errors.password && (
            <motion.p
              id="password-error"
              role="alert"
              className="mt-1.5 text-red-600 dark:text-red-400"
              style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: easeOutExpo }}
            >
              {errors.password.message}
            </motion.p>
          )}
        </motion.div>

        {/* Confirm password */}
        <motion.div variants={formItemVariants}>
          <motion.div
            className="relative group"
            variants={shakeVariants}
            animate={errors.confirmPassword ? 'error' : 'idle'}
          >
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a7d83] dark:text-[#62666d] group-focus-within:text-[#5e6ad2] dark:group-focus-within:text-[#7170ff] transition-colors pointer-events-none z-10"
              weight="regular"
            />
            <input
              id="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Confirm new password"
              aria-label="Confirm new password"
              aria-invalid={errors.confirmPassword ? true : undefined}
              aria-describedby={errors.confirmPassword ? 'confirm-password-error' : undefined}
              style={inputStyle}
              className={`${inputClass} pr-10 ${errors.confirmPassword ? errorBorderClass : ''}`}
              {...register('confirmPassword')}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
              aria-pressed={showConfirm}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7a7d83] hover:text-[#0f1011] dark:text-[#62666d] dark:hover:text-[#d0d6e0] transition-colors focus:outline-none z-10"
            >
              {showConfirm ? <EyeSlash className="h-4 w-4" weight="regular" /> : <Eye className="h-4 w-4" weight="regular" />}
            </button>
          </motion.div>
          {errors.confirmPassword && (
            <motion.p
              id="confirm-password-error"
              role="alert"
              className="mt-1.5 text-red-600 dark:text-red-400"
              style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: easeOutExpo }}
            >
              {errors.confirmPassword.message}
            </motion.p>
          )}
        </motion.div>

        {/* Submit */}
        <motion.button
          type="submit"
          disabled={isLoading}
          aria-busy={isLoading}
          className="w-full h-10 rounded-md bg-[#5e6ad2] text-white inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-[#5e6ad2]/40 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#0f1011]"
          style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
          variants={formItemVariants}
          whileHover={{ backgroundColor: '#7170ff', transition: { duration: 0.15 } }}
          whileTap={{ scale: 0.97, backgroundColor: '#5058c0' }}
        >
          {isLoading ? (
            <>
              {/* The button announces its own busy state — §55: don't double-speak. */}
              <DotLoader size="sm" announce={false} />
              <span>Resetting</span>
            </>
          ) : (
            <motion.span className="inline-flex items-center gap-2" whileHover="hover">
              Reset password
              <motion.span
                variants={{ hover: { x: 3 } }}
                transition={{ duration: 0.2, ease: easeOutExpo }}
                className="inline-flex"
              >
                <ArrowRight className="h-4 w-4" weight="bold" />
              </motion.span>
            </motion.span>
          )}
        </motion.button>

        {/* Back to sign in */}
        <motion.button
          type="button"
          onClick={() => navigate('/login')}
          className="w-full inline-flex items-center justify-center gap-1.5 text-[#5f6166] hover:text-[#0f1011] dark:text-[#8a8f98] dark:hover:text-[#d0d6e0] transition-colors"
          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
          variants={formItemVariants}
        >
          <ArrowLeft className="h-3.5 w-3.5" weight="bold" />
          Back to sign in
        </motion.button>
      </form>
    </motion.div>
  )
}

export default ResetPasswordPage
